import type { ReactNode } from "react";
import type { JsonObject } from "@/api/common";
import type { RegistryDatasetRef } from "@/api/training";
import {
  EvalRunDetailDatasetValue,
  EvalRunDetailInfo,
  EvalRunDetailInfoList,
  EvalRunDetailSection,
} from "./EvalRunDetailElements";
import { DETAIL_RUN_SERIES } from "./EvalRunResultSection";
import {
  type RunExecution,
  scenarioParameterRows,
  resolveRunExecution,
} from "./evalRunScenarioModel";
import { CompareTable, diffTableRow, resultSeriesColumns } from "./compare/CompareTable";
import { entryRows, type CompareDiffRow } from "./compare/evalCompareDiffModel";
import type { BaselineSeries } from "./evalBaselineModel";
import type { EvalRunDetailController } from "./useEvalRunDetailController";
import { EvalSuccessCriteria } from "./EvalSuccessCriteria";
import tableStyles from "./compare/CompareTable.module.css";
import styles from "./EvalRunDetailView.module.css";

export function EvalRunScenarioSection({
  scenarioName,
  datasetLabel,
  datasetRegistryRef,
  scenario,
  execution,
  params,
  metadataState,
  baseline,
  runConfig,
  onRetry,
  showSuccessCriteria = true,
}: {
  scenarioName: string | null;
  datasetLabel: string | null;
  datasetRegistryRef: RegistryDatasetRef | null | undefined;
  scenario: EvalRunDetailController["scenario"];
  execution: RunExecution;
  params: JsonObject | null;
  metadataState: EvalRunDetailController["metadataState"];
  baseline: BaselineSeries | null;
  // 이 실행 policy 를 학습한 config 다. Parameters 표의 "이 실행" 열 머리가 baseline 열과 같은 자리에 보인다.
  runConfig: string | null;
  onRetry: () => void;
  showSuccessCriteria?: boolean;
}): ReactNode {
  const { tag, task: taskName } = execution;
  const parameters = scenarioParameterRows(scenario, params, taskName);

  return (
    <EvalRunDetailSection title="Scenario">
      <EvalRunDetailInfoList>
        <EvalRunDetailInfo label="scenario" value={scenario?.name ?? scenarioName ?? "-"} />
        <EvalRunDetailInfo
          label="dataset"
          value={
            <EvalRunDetailDatasetValue
              upstreamLabel={datasetLabel}
              registryRef={datasetRegistryRef}
            />
          }
        />
        <EvalRunDetailInfo label="image" value={execution.image ?? "-"} />
        <EvalRunDetailInfo label="docker tag" value={tag ?? "-"} />
        <EvalRunDetailInfo
          label="상태"
          value={
            scenario === null ? (
              "-"
            ) : (
              <span className={scenario.is_active ? styles.okText : styles.muted}>
                {scenario.is_active ? "활성" : "비활성"}
              </span>
            )
          }
        />
      </EvalRunDetailInfoList>
      {/* 제목은 표의 groupHeader 가 표시한다. 칩 줄(flex)에 넣으면 표가 내용 폭으로 줄어든다. */}
      {parameters.length > 0 ? (
        <div className={styles.chipGroup}>
          <ParameterTable parameters={parameters} baseline={baseline} runConfig={runConfig} />
        </div>
      ) : null}
      {showSuccessCriteria ? (
        <EvalSuccessCriteria
          execution={execution}
          metadataState={metadataState}
          onRetry={onRetry}
        />
      ) : null}
    </EvalRunDetailSection>
  );
}

// 비교 창의 Scenario 표와 같은 모델을 쓴다. 같은 값은 흐리게 표시하고, 다른 값은 위로 올려 강조한다.
function ParameterTable({
  parameters,
  baseline,
  runConfig,
}: {
  parameters: ReadonlyArray<readonly [string, string]>;
  baseline: BaselineSeries | null;
  runConfig: string | null;
}): ReactNode {
  const baselineRun = baseline;
  const own = new Map(parameters);
  // baseline 은 비교 창과 같이 왼쪽에 표시한다.
  const rows: CompareDiffRow[] =
    baselineRun === null
      ? entryRows([own])
      : entryRows([
          new Map(
            scenarioParameterRows(
              baselineRun.run.scenario,
              baselineRun.run.params,
              resolveRunExecution(
                baselineRun.run.summaryRow,
                baselineRun.run.detail,
                baselineRun.run.scenario,
              ).task,
            ),
          ),
          own,
        ]);
  const runSeries = { ...DETAIL_RUN_SERIES, config: runConfig };
  const columns = resultSeriesColumns(
    baselineRun === null ? [runSeries] : [baselineRun, runSeries],
  );
  return (
    <CompareTable
      groupHeader="Parameters"
      leafHeader="항목"
      columns={columns}
      rows={rows.map((row) => diffTableRow(row, "PARAMETERS", row.key))}
      footer={
        baselineRun !== null && rows.some((row) => row.differs) ? (
          <p className={tableStyles.footnote}>Baseline 과 비교해 값이 다른 항목을 강조했습니다.</p>
        ) : null
      }
    />
  );
}
